import React, { useState } from 'react';
import { Container, Header, Message } from 'semantic-ui-react';
import { useQuery } from '@tanstack/react-query';
import I18n from '../lib/i18n';
import I18nHTMLTranslate from '../components/I18nHTMLTranslate';
import { apiV0Urls, viewUrls } from '../lib/requests/routes.js.erb';
import Loading from '../components/Requests/Loading';
import Errored from '../components/Requests/Errored';
import { fetchJsonOrError } from '../lib/requests/fetchWithAuthenticityToken';
import EditProfileFormHolder from '../components/ContactEditProfilePage/EditProfileFormHolder';
import useLoggedInUserPermissions from '../lib/hooks/useLoggedInUserPermissions';
import useQueryParams from '../lib/hooks/useQueryParams';
import useInputState from '../lib/hooks/useInputState';
import { IdWcaSearch } from '../components/SearchWidget/WcaSearch';
import SEARCH_MODELS from '../components/SearchWidget/SearchModel';
import WCAQueryClientProvider from '../lib/providers/WCAQueryClientProvider';

export default function Wrapper({ loggedInUserId, recaptchaPublicKey }) {
  return (
    <WCAQueryClientProvider>
      <ContactEditProfilePage
        loggedInUserId={loggedInUserId}
        recaptchaPublicKey={recaptchaPublicKey}
      />
    </WCAQueryClientProvider>
  );
}

function ContactEditProfilePage({ loggedInUserId, recaptchaPublicKey }) {
  const [queryParams] = useQueryParams();
  const editOthersProfileMode = Boolean(queryParams.editOthersProfile);
  const { data: loggedInUserData, isLoading, isError } = useQuery({
    queryKey: ['userData'],
    queryFn: () => fetchJsonOrError(apiV0Urls.users.me.userDetails),
    enabled: (
      // If the user is logged in and not editing somebody else's profile,
      // then we need to fetch their own WCA ID.
      !!loggedInUserId && !editOthersProfileMode
    ),
  });
  const { loggedInUserPermissions, loading } = useLoggedInUserPermissions();
  const [inputWcaId, setInputWcaId] = useInputState();
  const [contactSuccess, setContactSuccess] = useState(false);
  const [ticketId, setTicketId] = useState();

  const wcaId = editOthersProfileMode ? inputWcaId : loggedInUserData?.data?.user?.wca_id;

  if (isLoading || loading) return <Loading />;
  if (isError) return <Errored />;

  if (!loggedInUserId) {
    return (
      <Message error>
        <I18nHTMLTranslate i18nKey="page.contact_edit_profile.not_logged_in_error" />
      </Message>
    );
  }
  if (!editOthersProfileMode && loggedInUserData && !wcaId) {
    return (
      <Message error>
        <I18nHTMLTranslate i18nKey="page.contact_edit_profile.no_profile_error" />
      </Message>
    );
  }
  if (editOthersProfileMode && !loggedInUserPermissions.canRequestToEditOthersProfile) {
    return (
      <Message error>
        <I18nHTMLTranslate i18nKey="page.contact_edit_profile.no_permission_error" />
      </Message>
    );
  }
  if (contactSuccess) {
    if (loggedInUserPermissions.canRequestToEditOthersProfile && ticketId) {
      return (
        <Message success>
          <I18nHTMLTranslate
            i18nKey="page.contact_edit_profile.success_message_with_ticket_link_html"
            options={{ link: viewUrls.tickets.show(ticketId) }}
          />
        </Message>
      );
    }
    return (
      <Message
        success
        content={I18n.t('page.contact_edit_profile.success_message')}
      />
    );
  }

  return (
    <Container text>
      <Header as="h2">{I18n.t('page.contact_edit_profile.title')}</Header>
      {editOthersProfileMode && (
        <IdWcaSearch
          model={SEARCH_MODELS.person}
          multiple={false}
          value={inputWcaId}
          onChange={setInputWcaId}
          disabled={!!inputWcaId}
          label={I18n.t('page.contact_edit_profile.form.wca_id_search.label')}
        />
      )}
      {wcaId && (
        <EditProfileFormHolder
          wcaId={wcaId}
          editOthersProfileMode={editOthersProfileMode}
          onContactSuccess={(res) => {
            setContactSuccess(true);
            setTicketId(res?.ticket_id);
          }}
          recaptchaPublicKey={recaptchaPublicKey}
        />
      )}
    </Container>
  );
}
